import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";
import {
  Copy, Check, ExternalLink, Download, Link2, FileText,
  Image as ImageIcon, BookOpen, BarChart3, LayoutDashboard, Users,
  ScanLine, AlertCircle, Hash, CreditCard, Loader2, RotateCcw, Trash2, Save,
  PlayCircle, UserPlus, Handshake, Rocket, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { LOGO_URL } from "../lib/brand";
import AdminTopNav from "../components/AdminTopNav";
import { isPartner, isAdmin, getAuthHeaders, logout, loginUrl } from "../lib/adminAuth";
import { BACKEND_URL } from "../lib/apiBase";

const API = `${BACKEND_URL}/api`;
const WEBSITE = "https://alphalane-tradingsystems.com";

// Setup-unvollständig Erklärvideo (YouTube-Short, 9:16 hochkant).
// Wird solange angezeigt, bis Affiliate-Code + Partner-Account-Nummer + Reflink
// (Video 1 URL) alle drei im Partner-Profil hinterlegt sind.
const SETUP_INCOMPLETE_VIDEO_ID = "0UPDAspheH8";

const ALL_ASSETS = [
  { label: "Alphalane Logo (PNG)", url: "/Logo.png", icon: ImageIcon },
  { label: "LinkedIn-Banner — persönlich", url: "/linkedin-banner-personal.png", icon: ImageIcon },
  { label: "Insider-Report PDF", url: "/insider-report.pdf", icon: FileText },
  { label: "Mailserien-Katalog (zur Übersicht)", url: "/alphalane-mailserien.pdf", icon: FileText },
];

// Photo Social Media Templates moved to /partner/coaching — see PartnerCoaching.jsx
// (kept here only as a back-link; the grid + delete UI now lives in the Coaching tab).

const FAQ_ITEMS = [
  {
    q: "Wie funktioniert Alphalane?",
    a: "Eine KI-gestützte Software handelt vollautomatisch auf dem MT5-Konto des Kunden. Das Konto bleibt auf seinen Namen bei einem regulierten Broker (z.B. RoboForex). Wir haben nur Lese- und Handelszugriff — keine Auszahlungsrechte. Der Kunde behält jederzeit volle Kontrolle und kann jederzeit aussteigen."
  },
  {
    q: "Ist das Geld sicher? Wer hat Zugriff?",
    a: "Das Geld liegt auf dem Broker-Konto, das auf den Namen des Kunden läuft — nicht auf einem Alphalane-Konto. Wir haben ausschliesslich Read-Only-Zugriff plus Handelsausführung, KEINE Auszahlungsrechte. Auszahlungen kann nur der Kunde selbst via Broker veranlassen. Damit ist es technisch unmöglich, dass jemand das Geld 'mitnimmt'."
  },
  {
    q: "Welche Mindesteinlage empfiehlst du?",
    a: "Wir empfehlen ein Startkapital von 2.000–5.000 USD, um sinnvolle Diversifikation zu ermöglichen. Wichtig: nur Kapital einsetzen, dessen Verlust verkraftbar wäre. Für individuelle Empfehlungen → persönliches Gespräch."
  },
  {
    q: "Was sind die Kosten?",
    a: "Nur 40 % Performance Fee auf erzielte Gewinne — abgerechnet wochenweise auf dem persönlichen Dashboard. Keine Fixkosten, keine versteckten Gebühren, keine Verwaltungs- oder Setup-Gebühr. In Verlustmonaten fallen 0 € an."
  },
  {
    q: "Welche Rendite kann ich erwarten?",
    a: "Es gibt KEINE garantierten Renditen — wer dir das verspricht, lügt. Unsere historischen Ergebnisse zeigen eine kontinuierliche Entwicklung bei überschaubaren Rückschlägen. Genaue Zahlen siehst du auf der Track-Record-Seite. Bei der ersten Konversation mit deinem Lead: NIE konkrete Prozent-Zahlen versprechen."
  },
  {
    q: "Was passiert bei Verlusten / Drawdowns?",
    a: "Drawdowns sind normaler Bestandteil jedes Trading-Systems. Unsere KI ist mit hartem Risikomanagement gebaut: pro Trade fixe Stop-Loss-Levels, Diversifikation über mehrere Märkte. Der historische Max-Drawdown ist im Track Record transparent ersichtlich. Bei extremem Marktstress kann die KI auch komplett pausieren."
  },
  {
    q: "Kann ich jederzeit aussteigen?",
    a: "Ja — jederzeit, ohne Vertragslaufzeit, ohne Kündigungsfrist. Der Kunde widerruft einfach die Handelsvollmacht beim Broker (1 Klick) und kann sein Geld direkt auszahlen. Das Konto bleibt ohnehin auf seinem Namen."
  },
  {
    q: "Wann und wie kommen Auszahlungen?",
    a: "Auszahlungen veranlasst der Kunde selbst direkt beim Broker — das geht meist innerhalb 1–3 Werktagen. Gewinne werden wochenweise verbucht und können entweder ausgezahlt oder reinvestiert werden (Compounding-Effekt)."
  },
  {
    q: "Wie lange dauert das Setup?",
    a: "Komplett aufgesetzt in ca. 30–45 Minuten: Broker-Konto erstellen + verifizieren (KYC) → MT5 verbinden → Handelsvollmacht erteilen → Einzahlung tätigen. Genaue Anleitung mit Videos findet jeder Kunde nach Registrierung im Onboarding."
  },
  {
    q: "Muss ich technisch versiert sein?",
    a: "Nein. Die KI handelt vollautomatisch — der Kunde muss weder MT5 öffnen noch Trades platzieren. Er sieht nur sein Live-Dashboard. Setup wird Schritt-für-Schritt geführt, bei Fragen helfen wir persönlich."
  },
  {
    q: "Welcher Broker und warum?",
    a: "Wir nutzen RoboForex — reguliert, niedrige Spreads, schnelle Ausführung und etablierte API. Andere Broker sind technisch möglich, aber unsere KI ist für RoboForex optimiert."
  },
  {
    q: "Was mache ich, wenn der Lead 'zu teuer' findet?",
    a: "40 % Performance Fee bedeutet: WIR verdienen NUR, wenn DU verdienst. Es gibt keine Verwaltungsgebühren, keine Setup-Kosten, kein Risiko in Verlustmonaten. Im Vergleich zu klassischen Hedge Funds (2 % Fix + 20 % Performance) zahlst du bei uns 0 € auf das verwaltete Kapital."
  },
  {
    q: "Wie unterscheidet sich das von einem ETF?",
    a: "ETFs folgen dem Markt — wenn der Markt fällt, fällst du mit. Alphalane handelt market-neutral und kann in fallenden wie in steigenden Märkten Gewinne erzielen. Das Ziel ist eine unkorrelierte Renditequelle, NICHT der Ersatz eines ETFs. Ideale Kombination: Basis-Portfolio (ETF) + aktive Komponente (Alphalane)."
  },
  {
    q: "Wie ist die Steuer in CH / DE / AT geregelt?",
    a: "Gewinne unterliegen am Wohnsitz der Einkommens- bzw. Kapitalertragsteuer. Der Kunde erhält am Jahresende einen Broker-Report mit allen Trades und Gewinnen. Für die Steuererklärung bitte mit Steuerberater abklären — wir können keine steuerliche Beratung leisten."
  },
  {
    q: "Was wenn die KI 'kaputt geht'?",
    a: "Die KI läuft auf redundanten Cloud-Servern mit 24/7-Monitoring. Bei Auffälligkeiten greift automatisch ein Notabschaltungs-Mechanismus (Trading pausiert, Positionen werden risikofrei geschlossen). Das Kapital bleibt jederzeit am Broker und ist unabhängig von unserer Infrastruktur."
  },
  {
    q: "Wie schnell sehe ich erste Ergebnisse?",
    a: "Die KI handelt ab Verbindung des Kontos. Erste Trades meist innerhalb der ersten 24–48 Stunden. Sinnvolle Performance-Bewertung erst nach 4–6 Wochen — kurzfristige Schwankungen sind normal."
  },
  {
    q: "Was sage ich auf 'Ich überleg's mir und melde mich'?",
    a: "Zeige Verständnis und frag nach. Was hindert dich konkret daran heute zu starten? Meistens steckt eine offene Frage dahinter, die wir in 2 Minuten klären können. Frag konkret nach: Sicherheit, Kosten, Kapital — oder biete einen kurzen Call mit deinem Empfehlungsgeber an."
  },
  {
    q: "Wo melde ich Kunden an?",
    a: "Sende deinem Lead deinen persönlichen Reflink (oben rechts in deinem Dashboard sichtbar). Bei Registrierung wird der Lead automatisch dir zugeordnet — Provision läuft 1:1 über deinen Account."
  },
];

// WhatsApp template editor was removed from /partner/links — partners now edit
// their templates inline on /partner/coaching (per-template, not single global).


export default function AdminLinks() {
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(null);
  const [showTreeStats, setShowTreeStats] = useState(false);
  const [data, setData] = useState(null);
  const [pdfInfo, setPdfInfo] = useState(null);
  const [seedingDemo, setSeedingDemo] = useState(false);
  const [wipingDemo, setWipingDemo] = useState(false);
  const [syncingSlugs, setSyncingSlugs] = useState(false);

  // Photo Social Media Templates moved to /partner/coaching — see PartnerCoaching.jsx.

  const seedDemoAccounts = async () => {
    if (!window.confirm(
      "Demo-Konto resetten?\n\nLogin: noreply@alphalane-tradingsystems.com / AlphaAI2026\n\n" +
      "→ Customer-Dashboard mit 382 Tagen Equity-Verlauf wird neu generiert.\n" +
      "→ Partner-Account \"Alphalane AI\" mit 20 Downline-Partnern und 31 Kunden ($249'035 Volumen) wird neu aufgesetzt.\n\n" +
      "Bestehende echte Daten sind nicht betroffen (nur Demo-tagged Records werden ersetzt)."
    )) return;
    setSeedingDemo(true);
    try {
      const r = await axios.post(`${API}/admin/seed-demo`, {}, { headers: getAuthHeaders() });
      const s = r.data?.stats || {};
      toast.success(
        `✓ Demo zurückgesetzt — Equity $${Math.round(s.customer_equity).toLocaleString("de-CH")} · ` +
        `${s.direct_customers} direkte + ${s.downline_customers} Downline-Kunden`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Demo-Seed fehlgeschlagen.");
    } finally {
      setSeedingDemo(false);
    }
  };

  const wipeDemoAccounts = async (keepShowcase) => {
    const msg = keepShowcase
      ? "Alle Demo-Kunden + Downline-Partner löschen?\n\n" +
        "→ Behalten wird: noreply@alphalane-tradingsystems.com (Showcase-Login).\n" +
        "→ Entfernt: alle demo-customer-*@example.com Empfänger, fiktive Downline-Partner, deren Trade-Snapshots.\n\n" +
        "Eigene Live-Daten sind nicht betroffen. Aktion ist nicht umkehrbar."
      : "ALLE Demo-Daten komplett aus dieser Umgebung entfernen?\n\n" +
        "→ Demo-Customer (noreply@alphalane-…) wird gelöscht\n" +
        "→ Demo-Partner \u201eAlphalane AI\u201c wird gelöscht\n" +
        "→ Alle 31 Demo-Kunden + 20 Downline-Partner werden gelöscht\n" +
        "→ Alle Trade-Snapshots der Demo-Konten\n\n" +
        "Eigene Live-Daten sind nicht betroffen. Aktion ist nicht umkehrbar.";
    if (!window.confirm(msg)) return;
    setWipingDemo(true);
    try {
      const qs = keepShowcase ? "?keep_showcase=true" : "";
      const r = await axios.post(`${API}/admin/wipe-demo${qs}`, {}, { headers: getAuthHeaders() });
      const total = r.data?.total_deleted || 0;
      const detail = Object.entries(r.data?.deleted || {})
        .filter(([, n]) => n > 0)
        .map(([k, n]) => `${k}: ${n}`)
        .join(" · ") || "keine Treffer";
      toast.success(`✓ ${total} Demo-Datensätze gelöscht — ${detail}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Demo-Wipe fehlgeschlagen.");
    } finally {
      setWipingDemo(false);
    }
  };

  const syncSlugReflink = async () => {
    // First a dry-run preview.
    setSyncingSlugs(true);
    try {
      const dry = await axios.post(
        `${API}/admin/partners/sync-slug-reflink?dry_run=true`,
        {},
        { headers: getAuthHeaders() },
      );
      const wouldChange = dry.data?.would_change || 0;
      const skipped = dry.data?.skipped || 0;
      if (wouldChange === 0) {
        toast.success(`✓ Alle ${skipped} Partner sind bereits synchronisiert. Keine Änderung nötig.`);
        return;
      }
      // Preview the first 8 changes so the admin can see what's about to happen.
      const preview = (dry.data?.changes || []).slice(0, 8).map((c) =>
        `${c.name || c.email}: ${c.before.slug || "—"} / ${c.before.ref_link || "—"} → ${c.after.slug}`,
      ).join("\n");
      const more = wouldChange > 8 ? `\n…und ${wouldChange - 8} weitere` : "";
      if (!window.confirm(
        `${wouldChange} Partner werden synchronisiert (${skipped} bereits OK).\n\n` +
        `Beispiel-Änderungen:\n${preview}${more}\n\n` +
        `→ Keine Mails werden versendet.\n→ Demo-Accounts werden NICHT angefasst.\n\n` +
        `Fortfahren?`,
      )) {
        toast.info("Migration abgebrochen.");
        return;
      }
      const live = await axios.post(
        `${API}/admin/partners/sync-slug-reflink`,
        {},
        { headers: getAuthHeaders() },
      );
      const changed = live.data?.changed_count || 0;
      toast.success(`✓ ${changed} Partner synchronisiert. Keine Mails versendet.`, { duration: 15000 });
      console.log("[sync-slug-reflink]", live.data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.collisions) {
        const list = Object.entries(detail.collisions).map(([k, v]) => `${k}: ${v.join(", ")}`).join("\n");
        toast.error(`Kollisionen gefunden — bitte manuell auflösen:\n${list}`, { duration: 30000 });
      } else {
        toast.error(typeof detail === "string" ? detail : "Sync fehlgeschlagen.");
      }
    } finally {
      setSyncingSlugs(false);
    }
  };

  const fetchPdfInfo = async () => {
    try {
      const r = await axios.get(`${API}/admin/mailcatalog-pdf-info`, { headers: getAuthHeaders() });
      setPdfInfo(r.data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!isAdmin() && !isPartner()) {
      navigate(loginUrl());
      return;
    }
    if (isPartner()) {
      // Fetch fresh partner meta + stats
      (async () => {
        try {
          const r = await axios.get(`${API}/partner/stats`, { headers: getAuthHeaders() });
          setMeta(r.data.partner);
          setStats(r.data.stats);
          setData(r.data);
        } catch (e) {
          if (e.response?.status === 401) {
            const target = loginUrl();
            logout();
            navigate(target);
          }
        }
      })();
    } else {
      setMeta({ name: "Admin (Alphalane)", slug: null, ref_link: null });
    }
    fetchPdfInfo();
  }, [navigate]);

  // Generated links
  const reflinkSlug = meta?.slug || "zbtxc";
  const personalRegLink = `${WEBSITE}/register/${reflinkSlug}`;
  const personalOnboardingLink = `${WEBSITE}/bewerbung/${reflinkSlug}`;
  const personalLeadMagnetLink = meta?.slug
    ? `${WEBSITE}/ai/${meta.slug}`
    : `${WEBSITE}/insider-report`;
  const customerOnboardingLink = `${WEBSITE}/onboarding`;
  const customerDashboard = `${WEBSITE}/dashboard`;

  const visibleAssets = useMemo(() => ALL_ASSETS.filter((a) => !a.adminOnly || isAdmin()), []);

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      toast.success("Link kopiert!");
      setTimeout(() => setCopied(null), 1500);
    } catch {
      toast.error("Konnte nicht kopiert werden");
    }
  };

  // Mailserien-PDF wird vom Backend-Cron automatisch regeneriert — manueller
  // Re-Create-Button im UI entfernt (Mai 2026).

  const downloadQR = (canvasId, label) => {
    const canvas = document.getElementById(canvasId);
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.download = `alphalane-${label}-${reflinkSlug}.png`;
    a.href = url;
    a.click();
  };

  return (
    <div className="min-h-screen bg-[var(--al-bg)]" data-testid="admin-links">
      <header className="border-b border-[var(--al-line)] bg-white/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Alphalane" className="h-9 w-9 object-contain" />
            <div className="font-display font-semibold text-[15px]">
              <span className="al-grad-text">ALPHALANE</span>
              <span className="text-slate-500 text-xs ml-2 font-medium uppercase tracking-widest">{isPartner() ? "Partner · Links" : "Admin · Links"}</span>
            </div>
          </Link>
        </div>
      </header>

      <AdminTopNav />

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-8 space-y-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display font-bold text-3xl tracking-tight">Wichtige Links {meta?.name && <span className="text-slate-400">· {meta.name}</span>}</h1>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl">
              Alle relevanten Links, Materialien und Statistiken auf einen Blick.
            </p>
          </div>
          {isAdmin() && (
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={seedDemoAccounts}
                disabled={seedingDemo || wipingDemo}
                data-testid="seed-demo-btn"
                title="Setzt die Demo-Konten zurück (Customer + Partner Showcase). Eigene Live-Daten bleiben unverändert."
                className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-violet-200 text-violet-700 bg-violet-50 hover:bg-violet-100 text-xs font-semibold transition-colors disabled:opacity-60"
              >
                {seedingDemo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                {seedingDemo ? "Demo wird zurückgesetzt…" : "Demo-Daten resetten"}
              </button>
              <button
                type="button"
                onClick={() => wipeDemoAccounts(true)}
                disabled={seedingDemo || wipingDemo}
                data-testid="wipe-demo-soft-btn"
                title="Löscht alle Demo-Kunden + Downline-Partner, BEHÄLT aber den Showcase-Login (noreply@alphalane-tradingsystems.com)."
                className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-amber-200 text-amber-700 bg-amber-50 hover:bg-amber-100 text-xs font-semibold transition-colors disabled:opacity-60"
              >
                {wipingDemo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                Nur Demo-Kunden löschen
              </button>
              <button
                type="button"
                onClick={() => wipeDemoAccounts(false)}
                disabled={seedingDemo || wipingDemo}
                data-testid="wipe-demo-hard-btn"
                title="Entfernt ALLE Demo-Daten inkl. Showcase-Login — Produktions-Cleanup."
                className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-rose-200 text-rose-700 bg-rose-50 hover:bg-rose-100 text-xs font-semibold transition-colors disabled:opacity-60"
              >
                {wipingDemo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                Alle Demo-Daten löschen
              </button>
              <button
                type="button"
                onClick={syncSlugReflink}
                disabled={syncingSlugs}
                data-testid="sync-slug-reflink-btn"
                title="Vereinheitlicht slug + ref_link für alle echten Partner (nicht Demo). Sendet keine Mails."
                className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-sky-200 text-sky-700 bg-sky-50 hover:bg-sky-100 text-xs font-semibold transition-colors disabled:opacity-60"
              >
                {syncingSlugs ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />}
                {syncingSlugs ? "Synchronisiere…" : "Slugs ↔ Reflinks synchronisieren"}
              </button>
            </div>
          )}
        </div>

        {/* Partner statistics */}
        {isPartner() && stats && (() => {
          const tree = data?.tree_stats || null;
          const hasTree = Boolean(tree && (tree.downline_partners || tree.customers > stats.customers));
          const view = (showTreeStats && hasTree) ? tree : stats;
          const isTreeView = showTreeStats && hasTree;
          return (
            <section className="space-y-3" data-testid="partner-stats-wrapper">
              {hasTree && (
                <div className="flex items-center gap-2 flex-wrap" data-testid="partner-stats-toggle">
                  <button
                    type="button"
                    onClick={() => setShowTreeStats(false)}
                    data-testid="stats-view-direct"
                    className={`text-xs font-semibold uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors ${
                      !isTreeView
                        ? "bg-[var(--al-purple)] text-white border-[var(--al-purple)]"
                        : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    Nur direkte Kunden
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowTreeStats(true)}
                    data-testid="stats-view-tree"
                    className={`text-xs font-semibold uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors ${
                      isTreeView
                        ? "bg-[var(--al-purple)] text-white border-[var(--al-purple)]"
                        : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    Gesamte Struktur ({tree.downline_partners} Downline-Partner)
                  </button>
                  <Link
                    to="/partner/downline"
                    data-testid="open-partner-downline"
                    className="text-xs font-semibold uppercase tracking-widest px-3 py-1.5 rounded-full border bg-white text-[var(--al-purple)] border-[var(--al-purple)]/30 hover:bg-violet-50 transition-colors inline-flex items-center gap-1.5"
                  >
                    Meine Struktur anzeigen
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                </div>
              )}
              <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="partner-stats">
                <StatCard icon={<BarChart3 className="h-5 w-5" />} label="Link-Klicks" value={stats.clicks} />
                <StatCard icon={<Link2 className="h-5 w-5" />} label="Anfragen über mich" value={view.contacts} testid="partner-stat-contacts" />
                <StatCard icon={<Users className="h-5 w-5" />} label="Leads (Insider-Report)" value={view.leads} testid="partner-stat-leads" />
                <StatCard icon={<Users className="h-5 w-5" />} label={isTreeView ? "Kunden gesamt" : "Registrierte Kunden"} value={view.customers} testid="partner-stat-customers" />
                <StatCard
                  icon={<CreditCard className="h-5 w-5" />}
                  label={isTreeView ? "Pipeline-Volumen (gesamt)" : "Pipeline-Volumen (direkt)"}
                  value={`$${Math.round(view.total_balance || 0).toLocaleString("de-CH")}`}
                  testid="partner-stat-volume"
                />
              </div>
            </section>
          );
        })()}

        {/* Setup-vervollständigen Banner — partner self-service */}
        {isPartner() && <PartnerSetupBanner onComplete={() => window.location.reload()} />}

        {/* Personal reflink */}
        <section className="al-card p-6">
          <div className="flex items-center gap-2 mb-2">
            <Link2 className="h-4 w-4 text-[var(--al-purple)]" />
            <h2 className="font-display font-bold text-lg">Dein persönlicher Reflink</h2>
          </div>
          <p className="text-xs text-slate-500 mb-4">Verwende diesen Link in WhatsApp, E-Mails, Social Media, …</p>
          <div className="grid lg:grid-cols-[1fr_auto] gap-6 items-start">
            <div className="space-y-3 min-w-0">
              <LinkRow
                icon={FileText}
                accent="amber"
                label="Webinareinladung + Insider Report"
                description="Soft-Funnel: Interessent meldet sich fürs Live-Webinar an und erhält gleichzeitig den Insider-Report als PDF."
                useCase="Kalte Kontakte · LinkedIn · Kaltakquise"
                url={personalLeadMagnetLink}
                copied={copied === "insider"}
                onCopy={() => copy(personalLeadMagnetLink, "insider")}
              />
              <LinkRow
                icon={UserPlus}
                accent="emerald"
                label="Registrierung (für Endkunden)"
                description="Der Haupt-Reflink: potenzielle Kunden landen direkt auf der Registrierungs-Seite."
                useCase="WhatsApp · E-Mails · DMs · Social-Media-Bio"
                url={personalRegLink}
                copied={copied === "reg"}
                onCopy={() => copy(personalRegLink, "reg")}
              />
              <LinkRow
                icon={Handshake}
                accent="violet"
                label="Partner-Bewerbungs-Seite (für neue Partner)"
                description="Für Interessenten, die selbst Partner werden möchten. Sie reichen zuerst ihre Bewerbung ein — nach der Freischaltung erhalten sie den Onboarding-Link."
                useCase="Recruiting · Downline-Aufbau"
                url={personalOnboardingLink}
                copied={copied === "ponb"}
                onCopy={() => copy(personalOnboardingLink, "ponb")}
              />
              <LinkRow
                icon={Rocket}
                accent="blue"
                label="Kunden-Onboarding-Seite (Setup nach Registrierung)"
                description="Zum Nachfassen bei Kunden, die bereits registriert sind aber das MT5-Setup noch nicht abgeschlossen haben."
                useCase="Follow-Up nach Registrierung"
                url={customerOnboardingLink}
                copied={copied === "conb"}
                onCopy={() => copy(customerOnboardingLink, "conb")}
              />
              <LinkRow
                icon={LayoutDashboard}
                accent="indigo"
                label="Live-Dashboard (für Endkunden, nach Anmeldung)"
                description={`Zeigt dem Kunden seine Live-Performance. Sende diesen Link, wenn ein Kunde nachfragt „wo sehe ich meine Zahlen?".`}
                useCase="Bestandskunden-Support"
                url={customerDashboard}
                copied={copied === "dash"}
                onCopy={() => copy(customerDashboard, "dash")}
              />
            </div>
            <div className="grid grid-cols-2 gap-3 lg:flex lg:flex-row mx-auto lg:mx-0">
              <QrCard
                id="partner-qr-canvas"
                value={personalRegLink}
                eyebrow="Endkunden-Registrierung"
                hint="Endkunden scannen → landen direkt auf deiner Registrierungs-Seite."
                onDownload={() => downloadQR("partner-qr-canvas", "reflink")}
              />
              <QrCard
                id="insider-qr-canvas"
                value={personalLeadMagnetLink}
                eyebrow="Webinareinladung + Insider Report"
                hint={meta?.slug
                  ? "Scannen → Webinar-Anmeldung + Insider-Report. Leads werden dir automatisch zugeordnet."
                  : "Scannen → Webinar-Anmeldung + Insider-Report mit Mail-Eintrag."}
                onDownload={() => downloadQR("insider-qr-canvas", "webinar-insider")}
                testid="download-insider-qr"
              />
            </div>
          </div>
        </section>

        {/* WhatsApp-Vorlage removed (May 2026) — partners now edit social
            templates per-platform on /partner/coaching. */}

        {/* Quick links */}
        <section className="al-card p-6">
          <div className="flex items-center gap-2 mb-3">
            <LayoutDashboard className="h-4 w-4 text-[var(--al-purple)]" />
            <h2 className="font-display font-bold text-lg">Schnellzugriff</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            <QuickLink to="/admin" label="Anfragen & Leads" />
            <QuickLink to={isPartner() ? "/partner/onboarding" : "/admin/onboarding-customers"} label="Onboarding-Kunden" />
            <QuickLink to={customerDashboard} label="Kunden-Dashboard öffnen" external />
            <QuickLink to={WEBSITE + "/onboarding"} label="Kunden-Onboarding-Seite" external />
          </div>
        </section>

        {/* Marketing assets */}
        <section className="al-card p-6">
          <div className="flex items-center gap-2 mb-3">
            <ImageIcon className="h-4 w-4 text-[var(--al-purple)]" />
            <h2 className="font-display font-bold text-lg">Marketing-Materialien</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            {visibleAssets.map((a) => {
              const I = a.icon;
              const isMailCatalog = a.url === "/alphalane-mailserien.pdf";
              const mtimeLabel = isMailCatalog && pdfInfo?.mtime
                ? new Date(pdfInfo.mtime).toLocaleString("de-CH", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
                : null;
              return (
                <a
                  key={a.url}
                  href={a.url}
                  download
                  data-testid={`asset-${a.label}`}
                  className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-[var(--al-purple)] hover:bg-violet-50/40 transition-all group"
                >
                  <I className="h-4 w-4 text-slate-400 group-hover:text-[var(--al-purple)]" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">{a.label}</div>
                    {mtimeLabel && (
                      <div className="text-[10px] text-slate-400 mt-0.5" data-testid="mailcatalog-mtime">
                        Stand: {mtimeLabel}
                      </div>
                    )}
                  </div>
                  <Download className="h-4 w-4 text-slate-300 group-hover:text-[var(--al-purple)]" />
                </a>
              );
            })}
          </div>
        </section>

        {/* Social Media Templates — moved to /partner/coaching */}
        <section className="al-card p-6 bg-violet-50/40 border-violet-100" data-testid="social-templates-moved">
          <div className="flex items-center gap-2 mb-1">
            <ImageIcon className="h-4 w-4 text-[var(--al-purple)]" />
            <h2 className="font-display font-bold text-lg">Social Media Templates</h2>
          </div>
          <p className="text-sm text-slate-600 mt-2">
            Die Foto-Vorlagen sind in den Coaching-Bereich umgezogen — dort findest du sie zusammen mit Text-Vorlagen, Wording-Tipps und der Einwandbibliothek.
          </p>
          <Link
            to="/partner/coaching"
            data-testid="social-templates-go-to-coaching"
            className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[var(--al-purple)] hover:bg-violet-700 text-white text-sm font-display font-bold transition-colors"
          >
            Zum Coaching-Bereich <ExternalLink className="h-3.5 w-3.5" />
          </Link>
        </section>

        {/* FAQ */}
        <section className="al-card p-6" data-testid="faq-section">
          <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
            <div className="flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-[var(--al-purple)]" />
              <h2 className="font-display font-bold text-lg">FAQ — häufige Einwände & Antworten</h2>
            </div>
            <span className="text-[11px] uppercase tracking-widest font-bold text-slate-400">
              {FAQ_ITEMS.length} Antworten
            </span>
          </div>
          <p className="text-xs text-slate-500 mb-4 leading-relaxed">
            Dein Cheat-Sheet für Kundengespräche — die häufigsten Fragen mit ausformulierten Antworten,
            die du 1:1 übernehmen oder anpassen kannst.
          </p>
          <div className="divide-y divide-slate-100">
            {FAQ_ITEMS.map((it, i) => (
              <details key={it.q} className="py-3 group" data-testid={`faq-${i}`}>
                <summary className="cursor-pointer font-display font-semibold text-sm text-slate-900 group-open:text-[var(--al-purple)] flex items-start gap-2">
                  <span className="text-[11px] font-mono-tab text-slate-400 group-open:text-[var(--al-purple)] mt-0.5 flex-shrink-0 w-6">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="flex-1">{it.q}</span>
                </summary>
                <p className="mt-2 ml-8 text-sm text-slate-600 leading-relaxed">{it.a}</p>
              </details>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

// Accent color map — each link type gets a distinct color-coded left border
// and icon badge so partners can spot the right link at a glance.
const LINK_ACCENTS = {
  emerald: { border: "border-l-emerald-500", bg: "bg-emerald-50", icon: "text-emerald-600", chip: "bg-emerald-100 text-emerald-700" },
  violet:  { border: "border-l-violet-500",  bg: "bg-violet-50",  icon: "text-violet-600",  chip: "bg-violet-100 text-violet-700" },
  amber:   { border: "border-l-amber-500",   bg: "bg-amber-50",   icon: "text-amber-600",   chip: "bg-amber-100 text-amber-700" },
  blue:    { border: "border-l-blue-500",    bg: "bg-blue-50",    icon: "text-blue-600",    chip: "bg-blue-100 text-blue-700" },
  indigo:  { border: "border-l-indigo-500",  bg: "bg-indigo-50",  icon: "text-indigo-600",  chip: "bg-indigo-100 text-indigo-700" },
};

const LinkRow = ({ icon: Icon = Link2, accent = "violet", label, description, useCase, url, copied, onCopy }) => {
  const c = LINK_ACCENTS[accent] || LINK_ACCENTS.violet;
  return (
    <div
      className={`flex items-stretch gap-3 rounded-xl border border-slate-200 border-l-4 ${c.border} bg-white overflow-hidden`}
      data-testid={`link-row-${(label || "").split(" ")[0].toLowerCase()}`}
    >
      {/* Icon badge — visually anchors the link type */}
      <div className={`flex-shrink-0 flex items-center justify-center w-14 ${c.bg}`}>
        <Icon className={`h-6 w-6 ${c.icon}`} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 py-3 pr-2">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <div className="font-display font-bold text-sm text-slate-800">{label}</div>
          {useCase && (
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${c.chip}`}>
              {useCase}
            </span>
          )}
        </div>
        {description && (
          <p className="text-xs text-slate-500 mb-1.5 leading-snug">{description}</p>
        )}
        <div className="text-[11px] font-mono text-slate-500 truncate">{url}</div>
      </div>

      {/* Actions */}
      <div className="flex-shrink-0 flex items-center gap-1 pr-2">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="p-2 text-slate-400 hover:text-[var(--al-purple)] hover:bg-slate-50 rounded-lg transition-colors"
          title="Link öffnen"
        >
          <ExternalLink className="h-4 w-4" />
        </a>
        <button
          onClick={onCopy}
          className={`p-2 rounded-lg transition-colors ${copied ? "text-emerald-500 bg-emerald-50" : "text-slate-400 hover:text-[var(--al-purple)] hover:bg-slate-50"}`}
          data-testid={`copy-${(label || "").split(" ").join("-")}`}
          title="Kopieren"
        >
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
};

const QrCard = ({ id, value, eyebrow, hint, onDownload, testid }) => (
  <div className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-violet-50/40 border border-violet-100 w-full max-w-[200px]">
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-[var(--al-purple)] mb-1 text-center leading-tight">
      <ScanLine className="h-3 w-3 flex-shrink-0" /> <span>{eyebrow}</span>
    </div>
    <QRCodeCanvas
      id={id}
      value={value}
      size={140}
      level="H"
      marginSize={2}
      bgColor="#FFFFFF"
      fgColor="#0F172A"
      imageSettings={{ src: "/Logo.png", height: 36, width: 36, excavate: true }}
    />
    <p className="text-[10px] text-slate-500 text-center leading-tight max-w-[140px]">{hint}</p>
    <button
      onClick={onDownload}
      data-testid={testid || `download-${id}`}
      className="text-xs font-semibold text-[var(--al-purple)] hover:underline flex items-center gap-1"
    >
      <Download className="h-3 w-3" /> QR-Code laden
    </button>
  </div>
);

const QuickLink = ({ to, label, external }) => external ? (
  <a href={to} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-3 rounded-lg border border-slate-200 hover:border-[var(--al-purple)] text-sm group transition-all">
    <span className="flex-1 text-slate-700 group-hover:text-slate-900">{label}</span>
    <ExternalLink className="h-4 w-4 text-slate-300 group-hover:text-[var(--al-purple)]" />
  </a>
) : (
  <Link to={to} className="flex items-center gap-2 p-3 rounded-lg border border-slate-200 hover:border-[var(--al-purple)] text-sm group transition-all">
    <span className="flex-1 text-slate-700 group-hover:text-slate-900">{label}</span>
    <ExternalLink className="h-4 w-4 text-slate-300 group-hover:text-[var(--al-purple)]" />
  </Link>
);

const StatCard = ({ icon, label, value, testid }) => (
  <div className="al-card p-5" data-testid={testid}>
    <div className="flex items-center gap-2 text-slate-500 text-[11px] uppercase tracking-widest font-bold">
      {icon} <span>{label}</span>
    </div>
    <div className="mt-2 font-display font-bold text-3xl tracking-tight">{value ?? 0}</div>
  </div>
);


const PartnerSetupBanner = ({ onComplete }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState("");
  const [refLink, setRefLink] = useState("");
  const [mt5, setMt5] = useState("");
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/partner/setup-status`, { headers: getAuthHeaders(), withCredentials: true });
      setStatus(r.data);
      setCode(r.data.affiliate_code || "");
      setRefLink(r.data.ref_link || "");
      setMt5(r.data.payout_mt5 || "");
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (loading || !status || status.complete) return null;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (code !== (status.affiliate_code || "")) payload.affiliate_code = code;
      if (refLink !== (status.ref_link || "")) payload.ref_link = refLink;
      if (mt5 !== (status.payout_mt5 || "")) payload.payout_mt5 = mt5;
      if (Object.keys(payload).length === 0) {
        toast.info("Nichts zu speichern.");
        return;
      }
      const r = await axios.patch(`${API}/partner/setup`, payload, { headers: getAuthHeaders(), withCredentials: true });
      toast.success(r.data.complete ? "Setup abgeschlossen! 🎉 Dein Reflink ist jetzt aktiv." : "Daten gespeichert.");
      setStatus(r.data);
      if (r.data.complete && onComplete) onComplete();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const missingLabels = (status.missing || []).map((m) => {
    if (m === "affiliate_code") return "RoboForex Partner-Account (Nummer, z.B. 30291457)";
    if (m === "ref_link") return "Persönlicher Affiliate-Code (z.B. zbtxc)";
    if (m === "mt5_partner_id") return "Reflink Video 2 (Copy-Trading-Link von RoboForex)";
    if (m === "payout_mt5") return "MT5-Account-Nummer für Provisions-Auszahlung";
    return m;
  });

  return (
    <section
      data-testid="partner-setup-banner"
      className="al-card overflow-hidden border-amber-200 bg-gradient-to-br from-amber-50/80 via-white to-orange-50/40"
    >
      <div className="p-5 sm:p-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 h-11 w-11 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-md shadow-amber-200">
            <AlertCircle className="h-5 w-5 text-white" strokeWidth={2.2} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
              <h2 className="font-display font-bold text-lg sm:text-xl leading-tight tracking-tight text-slate-900">
                Setup unvollständig — dein Reflink ist noch nicht aktiv.
              </h2>
              <Link
                to="/partner/profile"
                data-testid="partner-setup-goto-profile"
                className="text-xs font-semibold text-[var(--al-purple)] hover:underline whitespace-nowrap"
              >
                Jetzt nachreichen →
              </Link>
            </div>
            <p className="text-sm text-slate-700 leading-relaxed">
              Folgende Daten fehlen, damit dein Reflink live geht und du Provisionen erhalten kannst:
            </p>
            <ul className="mt-2 ml-5 list-disc text-sm text-slate-800 font-display font-semibold leading-relaxed">
              {missingLabels.map((m) => <li key={m}>{m}</li>)}
            </ul>

            {/* Setup-Erklärvideo (YouTube-Short, hochkant) */}
            <div className="mt-4" data-testid="partner-setup-video-slot">
              {SETUP_INCOMPLETE_VIDEO_ID ? (
                <div className="flex justify-center">
                  <div
                    className="relative w-full max-w-[320px] rounded-2xl overflow-hidden bg-slate-900 shadow-lg ring-1 ring-slate-200"
                    style={{ aspectRatio: "9 / 16" }}
                  >
                    <iframe
                      src={`https://www.youtube.com/embed/${SETUP_INCOMPLETE_VIDEO_ID}?rel=0&modestbranding=1&playsinline=1`}
                      title="Partner-Setup Anleitung"
                      className="absolute inset-0 w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                </div>
              ) : (
                <div className="relative w-full rounded-xl overflow-hidden bg-gradient-to-br from-slate-100 to-amber-50/60 border border-dashed border-amber-300 flex items-center justify-center" style={{ aspectRatio: "16 / 9" }}>
                  <div className="text-center px-4">
                    <PlayCircle className="h-10 w-10 text-amber-500 mx-auto mb-2" strokeWidth={1.6} />
                    <div className="font-display font-semibold text-slate-700 text-sm">
                      Erklär-Video folgt in Kürze
                    </div>
                  </div>
                </div>
              )}
              <p className="mt-2 text-center text-[11px] text-slate-500 leading-relaxed max-w-md mx-auto">
                Kurzanleitung: So findest du deinen Reflink, Affiliate-Code und die Partner-Account-Nummer im RoboForex-Partnerbereich.
              </p>
            </div>

            {expanded && (
              <div className="mt-5 grid sm:grid-cols-2 gap-3" data-testid="partner-setup-form">
                {status.missing.includes("affiliate_code") && (
                  <label className="block">
                    <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1.5">
                      <Hash className="h-3.5 w-3.5" /> RoboForex Partner-Account
                    </span>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      placeholder="z.B. 30291457"
                      data-testid="partner-setup-affiliate-code"
                      className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-mono-tab focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-amber-400 transition"
                    />
                    <p className="mt-1 text-[11px] text-slate-500">
                      Deine 6–10-stellige RoboForex Partner-Account-Nummer. Hierher werden Provisionen ausgezahlt.
                    </p>
                  </label>
                )}
                {status.missing.includes("ref_link") && (
                  <label className="block">
                    <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1.5">
                      <Link2 className="h-3.5 w-3.5" /> Persönlicher Reflink-Code
                    </span>
                    <input
                      type="text"
                      value={refLink}
                      onChange={(e) => setRefLink(e.target.value)}
                      placeholder="z.B. zbtxc"
                      maxLength={50}
                      data-testid="partner-setup-reflink"
                      className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-mono-tab uppercase tracking-wide focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-amber-400 transition"
                    />
                    <p className="mt-1 text-[11px] text-slate-500">
                      Dein kurzer Affiliate-Code aus dem RoboForex-Dashboard (3–8 Zeichen). Damit werden Endkunden deinem Konto zugeordnet.
                    </p>
                  </label>
                )}
                {status.missing.includes("payout_mt5") && (
                  <label className="block">
                    <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1.5">
                      <CreditCard className="h-3.5 w-3.5" /> MT5-Account-Nr. (Provisions-Auszahlung)
                    </span>
                    <input
                      type="text"
                      value={mt5}
                      onChange={(e) => setMt5(e.target.value)}
                      placeholder="z.B. 9123456"
                      data-testid="partner-setup-mt5"
                      className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-mono-tab focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-amber-400 transition"
                    />
                    <p className="mt-1 text-[11px] text-slate-500">
                      Nummer des MT5-Trading-Accounts bei RoboForex, auf den deine Provision ausgezahlt wird.
                    </p>
                  </label>
                )}
                <div className="sm:col-span-2 flex items-center justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={save}
                    disabled={saving}
                    data-testid="partner-setup-save"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white font-display font-bold text-sm transition-colors shadow-md shadow-amber-200"
                  >
                    {saving ? (
                      <><Loader2 className="h-4 w-4 animate-spin" /> Speichere…</>
                    ) : (
                      <><Save className="h-4 w-4" /> Daten speichern</>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
